import { useState, useCallback } from 'react';
import { ShapeFactory } from '../ShapeFactory';
import { parseSvg } from '../utils/svgParser';

export const useProjectIO = (gridOffset, setGridOffset, gridAreaRef, setSelectedObjects, setMode, transform) => {
    const [isSelectingArea, setIsSelectingArea] = useState(false);
    const [selectionArea, setSelectionArea] = useState(null);
    const [svgText, setSvgText] = useState(null);

    const handleSaveProject = useCallback(async () => {
        const projectData = {
            version: "1.0",
            grid: {
                width: 600,
                height: 400,
                offset: gridOffset
            },
            objects: gridAreaRef.current?.getGraphicObjects().map(obj => obj.toJSON()) || []
        };

        const json = JSON.stringify(projectData, null, 2);

        if ('showSaveFilePicker' in window) {
            try {
                const handle = await window.showSaveFilePicker({
                    suggestedName: 'project.json',
                    types: [{
                        description: 'JSON Files',
                        accept: { 'application/json': ['.json'] },
                    }],
                });
                const writable = await handle.createWritable();
                await writable.write(json);
                await writable.close();
            } catch (err) {
                if (err.name !== 'AbortError') {
                    console.error("Failed to save file using picker", err);
                }
            }
        } else {
            const blob = new Blob([json], { type: 'application/json' });
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = 'project.json';
            link.click();
            URL.revokeObjectURL(url);
        }
    }, [gridOffset, gridAreaRef]);

    const handleLoadProject = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.json';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const data = JSON.parse(event.target.result);
                    if (data.objects) {
                        const restoredObjects = data.objects.map(objData => ShapeFactory.fromJSON(objData)).filter(o => o !== null);
                        gridAreaRef.current?.clearDrawings();
                        gridAreaRef.current?.modifyObjects([], restoredObjects);

                        if (data.grid && data.grid.offset) {
                            setGridOffset(data.grid.offset);
                        }

                        setSelectedObjects([]);
                    }
                } catch (err) {
                    console.error("Failed to parse project file", err);
                    alert("Error loading project: Invalid file format.");
                }
            };
            reader.readAsText(file);
        };
        input.click();
    }, [setGridOffset, gridAreaRef, setSelectedObjects]);

    const handleImportSVG = useCallback(() => {
        const input = document.createElement('input');
        input.type = 'file';
        input.accept = '.svg';
        input.onchange = (e) => {
            const file = e.target.files[0];
            if (!file) return;

            const reader = new FileReader();
            reader.onload = (event) => {
                try {
                    const svgText = event.target.result;
                    setSvgText(svgText);
                    setIsSelectingArea(true);
                    setMode('select');
                } catch (err) {
                    console.error("Failed to parse SVG file", err);
                    alert("Error loading SVG: Invalid file format.");
                }
            };
            reader.readAsText(file);
        };
        input.click();
    }, [setMode]);

    const performSvgPlacement = useCallback(() => {
        if (!svgText || !selectionArea) return;
        const shapes = parseSvg(svgText, selectionArea, transform);
        gridAreaRef.current?.modifyObjects([], shapes);
        setIsSelectingArea(false);
        setSelectionArea(null);
        setSvgText(null);
    }, [svgText, selectionArea, transform, gridAreaRef]);

    const handleImportSVGText = useCallback((text) => {
        setSvgText(text);
        setIsSelectingArea(true);
        setMode('select');
    }, [setMode]);

    return {
        isSelectingArea,
        setIsSelectingArea,
        selectionArea,
        setSelectionArea,
        svgText,
        setSvgText,
        handleSaveProject,
        handleLoadProject,
        handleImportSVG,
        handleImportSVGText,
        performSvgPlacement
    };
};
